import type { Governance } from "../../types/Governance";
import {
  RateRule,
  rateRulesToApi,
} from "@components/GovernanceEffort/rateRules";

export interface ProjectGovernanceFields {
  /** Absent on create. */
  id?: string;
  projectId: string | number;
  description: string;
  /** Whole-number percentage as typed, e.g. 12.5 for 12.5%. */
  percentage: string | number;
  hours: number;
  calculationType: Governance["calculationType"];
  allocationMethod: Governance["allocationMethod"];
  assignEffortToService: boolean;
  filterType: string;
  filterId: string | null;
  variables: { name: string; value: unknown }[];
  /** 0 when no phase is picked. */
  phaseId: string | number;
  /** 0 when no category is picked. */
  categoryId: string | number;
  /** A renderResourceOptions value ("project-resources-3", "resources-5",
   *  "resource-rates-8"), or 0 when none is picked. */
  resourceValue: string | number;
  /** release.configurable-governance. Off: exactly the v1 form's payload. */
  configurable?: boolean;
  rateRules?: RateRule[];
}

const relationship = (type: string, id: string | number) =>
  id === 0 || id === "0" || id == null
    ? undefined
    : { data: { type, id: String(id) } };

// A project resource goes by its own id; any other option's composite value
// is passed through whole, and the API's ProjectResourceUpsertable (v1 and
// v2 alike) parses it into a resource or resource rate.
const projectResourceId = (value: string | number) => {
  const text = String(value);
  const prefix = "project-resources-";
  return text.startsWith(prefix) ? text.slice(prefix.length) : text;
};

/** The v2 project-governance payload, with the attributes the v1 form sent. */
export const buildProjectGovernanceData = (
  fields: ProjectGovernanceFields
) => ({
  type: "project-governances",
  ...(fields.id ? { id: fields.id } : {}),
  attributes: {
    description: fields.description,
    // Flag off: whole percents, as the v1 form always sent. On: the API's 4
    // fraction digits, so 12.5% stays 12.5%.
    rate: fields.configurable
      ? Number((Number(fields.percentage) / 100).toFixed(4))
      : (Number(fields.percentage) / 100).toFixed(2),
    "fixed-hours": fields.hours,
    "calculation-type": fields.calculationType,
    "allocation-method": fields.allocationMethod,
    "assign-effort-to-service": fields.assignEffortToService,
    "filter-type": fields.filterType,
    "filter-id": fields.filterId,
    "project-variables": fields.variables,
    // minimum-hours is never sent: a read-only snapshot on project governance.
    ...(fields.configurable && {
      // Rules only apply to percent of total; clear them like the settings form.
      "rate-rules":
        fields.calculationType === "percent_of_total"
          ? rateRulesToApi(fields.rateRules ?? [])
          : [],
    }),
  },
  relationships: {
    project: relationship("projects", fields.projectId),
    "project-phase": relationship("project-phases", fields.phaseId),
    "service-category": relationship("service-categories", fields.categoryId),
    "project-resource":
      fields.resourceValue === 0 || fields.resourceValue === "0"
        ? undefined
        : relationship(
            "project-resources",
            projectResourceId(fields.resourceValue)
          ),
  },
});
